import _ from 'lodash';
import config from 'config';
import moment from 'moment';
import { toast } from 'react-toastify';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Auth } from 'aws-amplify';
import posthog from 'posthog-js';
import stringify from 'qs-stringify';

import { getJwt, getUserByToken } from 'app/crud/auth.crud';
import {
  accessDeniedErrorMessages,
  getHeaders,
  getHeadersNoAuth,
  postRequestWrapper,
  request,
} from 'app/utils/fetch';
import { sentry } from 'app/utils/sentry';
import { notify } from 'app/Services/app/notificationService';
import { actions as ProjectActions } from '../projects/actions';
import { AuthActionTypes as actionTypes } from './actionTypes';
import { actions } from './actions';
import { TAuthAction } from './types';

export function* watchAuthSaga() {
  yield takeLatest(actionTypes.Login, loginSaga);
  yield takeLatest(actionTypes.FetchUserDetails, FetchUserDetailsSaga);
  yield takeLatest(actionTypes.AdminFetchListUser, AdminFetchListUserSaga);
  yield takeLatest(actionTypes.GetGoogleAuthUrl, apiGetGoogleAuthUrl);
  yield takeLatest(actionTypes.RevokeToken, revokeTokenSaga);
  yield takeLatest(
    actionTypes.GetAmazonAdvertisingAuthUrl,
    apiGetAmazonAdvertisingAuthUrl,
  );
  yield takeLatest(
    actionTypes.RevokeAmazonAdvertisingAccount,
    revokeAmazonAdvertisingSaga,
  );
  yield takeLatest(
    actionTypes.RevokeAmazonSellerIntegration,
    revokeAmazonSellerIntegrationSaga,
  );
  yield takeLatest(actionTypes.UpdateAccountName, updateAccountName);
  yield takeLatest(actionTypes.SelectUser, SelectUser);
  yield takeLatest(actionTypes.RequestCsv, requestCsv);
  yield takeLatest(actionTypes.FetchCsv, fetchCsv);

  //delete RP

  yield takeLatest(actionTypes.DeleteCsv, apiDeleteReportSaga);
  yield takeLatest(actionTypes.Register, registerSaga);
  yield takeLatest(actionTypes.UserRequested, userRequested);
  yield takeLatest(
    actionTypes.FetchAsinPriceDetails,
    apiFetchAsinPriceDetailsSaga,
  );
  yield takeLatest(actionTypes.FetchAsinsAnalysis, apiFetchAsinsAnalysisSaga);
  yield takeLatest(actionTypes.FetchAsinsDetails, apiFetchAsinsDetailsSaga);

  yield takeLatest(actionTypes.GetUserSettings, apiGetUserSettingsSaga);
  yield takeLatest(actionTypes.UpdateUserSettings, apiUpdateUserSettings);
  yield takeLatest(
    actionTypes.SetAccessDeniedError,
    notificationAccessDeniedErrorMessage,
  );
  yield takeLatest(actionTypes.SignUp, signUpSaga);
  yield takeLatest(actionTypes.ForgotPassword, forgotPasswordSaga);
  yield takeLatest(actionTypes.ForgotPasswordConfirm, forgotPasswordConfirmSaga);
  yield takeLatest(actionTypes.UpdateAccountSettings, updateAccountSettingsSaga);
  yield takeLatest(actionTypes.VerifyEmailChange, verifyEmailChangeSaga);
  yield takeLatest(actionTypes.UpdateConnectionName, updateConnectionNameSaga);
}

function* loginSaga(action) {
  const data = yield Auth.currentSession();

  const headers = yield getHeaders();
  yield call(() => request(headers).get('/get-customer')); // Get and set user's plan when first login
  const userPlan = yield select((state) => state.auth?.userPlan?.name);
  posthog.identify(data.idToken.payload.sub, {
    user_id: data.idToken.payload.sub,
    email: data.idToken.payload.email,
    userPlan,
  });
  const { payload } = data.getIdToken();
  const user = payload;
  const token = yield getJwt();
  const requestPayload = {
    email: user.email,
  };
  yield postRequestWrapper('/auth/email', token, requestPayload);
  yield postRequestWrapper('/auth/clear-posthog-flags', token, {
    sub: data.idToken.payload.sub,
  });

  yield put(actions.fulfillUser(user));

  const apiRes = yield call(() =>
    request(headers).get('/retrieve-customer-payment'),
  );
  const invoices = yield call(() => request(headers).get('/invoices'));

  if (
    (invoices &&
      invoices.length > 0 &&
      invoices[invoices.length - 1].status === 'failed') ||
    (apiRes && apiRes.status === 'OK' && apiRes?.data?.status === 'past_due')
  ) {
    window.location.href = '/settings/billing';
    return;
  }

  yield put(actions.disableLoading());
}

function* FetchUserDetailsSaga(action) {
  try {
    yield put(actions.enableLoading());
    const headers = yield getHeaders();
    const response = yield call(() => request(headers).get('/users/details'));
    if (response && response?.sub) {
      const { settings, ...userDetails } = response;
      localStorage.setItem('userDetails', JSON.stringify(userDetails));
      yield put(actions.setUserDetails());
      yield put(actions.setUserSettings(settings));
    }
    if (action?.callback && typeof action?.callback === 'function') {
      action.callback();
    }
  } catch (error) {
    console.error('Error fetching user details:', error);
    if (action?.callback && typeof action?.callback === 'function') {
      action.callback(error);
    }
  } finally {
    yield put(actions.disableLoading());
  }
}

function* AdminFetchListUserSaga(action) {
  yield put(actions.disableSendButtonState());
  const requestPayload = {};
  const token = yield getJwt();
  const apiRes = yield postRequestWrapper('/list-user', token, requestPayload);
  if (apiRes) {
    yield put(actions.updateListUser(apiRes));
  }
  yield put(actions.disableSendButtonState(false));
}

function* SelectUser(action) {
  yield put(ProjectActions.fetchProjectDetails(action.payload));
}

function* requestCsv(action) {
  yield put(actions.setDownloadingCSVState(true));
  const requestPayload = {
    reportType: action.payload.reportType,
    keywordASIN: action.payload.keywordASIN,
    from: moment(action.payload.from).format('YYYY-MM-DD'),
    to: moment(action.payload.to).format('YYYY-MM-DD'),
    rangeType: action.payload.rangeType,
    searchType: action.payload.searchType,
    exactAsinOnly: action.payload.exactAsin,
    onlyOneKeyword: action.payload.onlyOneKeyword,
    showRanks: action.payload.showRanks,
    getSimilarASINs: action.payload.getSimilarASINs,
    projectId: action.payload.projectId,
    order: action.payload.order,
    orderBy: action.payload.orderBy,
    selectedUserSub: action.payload.selectedUserSub,
    keywords: action.payload.keywords,
    marketplace: action.payload.marketplace,
  };

  // Remove properties with undefined values
  const filteredRequestPayload = Object.fromEntries(
    Object.entries(requestPayload).filter(([_, value]) => value !== undefined),
  );

  // Only use for testing page keywords-relevancy
  if (filteredRequestPayload.reportType === 8) {
    filteredRequestPayload.keywords = action.payload.keywords;
  }
  const headers = yield getHeaders();
  yield call(() =>
    request(headers).post('/download/add', stringify(filteredRequestPayload)),
  );
  if (_.isFunction(action.cb)) {
    action.cb(null);
  }
  yield put(actions.setDownloadingCSVState(false));
}

function* updateAccountName(action) {
  yield put(actions.enableLoading());
  const requestPayload = {
    name: action.payload.accountName,
  };
  const headers = yield getHeaders();
  yield call(() =>
    request(headers).put('/account-names', stringify(requestPayload)),
  );
  if (_.isFunction(action.cb)) {
    action.cb(null);
  }
  yield put(actions.fetchUserDetails());
  yield put(actions.disableLoading());
}

function* fetchCsv(action) {
  yield put(actions.updateIsFetchingCsvData(true));
  const headers = yield getHeaders();
  const apiRes = yield call(() => request(headers).get('/downloads'));
  if (apiRes) {
    yield put(actions.updateCsvData(apiRes));
  }
  yield put(actions.updateIsFetchingCsvData(false));
}

function* apiDeleteReportSaga(action) {
  yield put(actions.updateIsFetchingCsvData(true));

  const token = yield getJwt();
  const requestPayload = action.payload;
  const apiRes = yield postRequestWrapper(
    '/report/delete',
    token,
    requestPayload,
  );
  if (apiRes) {
    yield put(actions.deleteReportSuccess(apiRes));
  }
  yield put(actions.updateIsFetchingCsvData(false));
}

function* registerSaga() {
  yield put(actions.requestUser());
}

function* userRequested() {
  const { data: user } = yield getUserByToken();

  yield put(actions.fulfillUser(user));
}

function* apiFetchAsinPriceDetailsSaga(action) {
  const token = yield getJwt();
  const requestPayload = {
    ASIN: action.payload.ASIN,
    marketplace: action.payload.marketplace || 'US',
  };
  let apiRes = yield postRequestWrapper(
    '/asin-price-detail',
    token,
    requestPayload,
  );
  if (apiRes) {
    if (_.get(apiRes, 'status', '') === 'fail') {
      apiRes = { ...requestPayload, ...apiRes };
    }
    yield put(actions.updateAsinPriceDetails({ apiRes }));
  }
}

function* apiFetchAsinsAnalysisSaga(action) {
  yield put(actions.disableSendButtonState());
  const token = yield getJwt();
  const requestPayload = {
    arASINs: action.payload.arASINs,
    startDate: moment(action.payload.startDate).format('YYYY-MM-DD'),
    endDate: moment(action.payload.endDate).format('YYYY-MM-DD'),
    exactAsinOnly: action.payload.exactAsin,
    rangeType: action.payload.rangeType.toLowerCase(),
  };
  const apiRes = yield postRequestWrapper(
    '/competitor-analysis',
    token,
    requestPayload,
  );
  if (apiRes) {
    // yield put(actions.updateResultsAsinsAnalysis({ apiRes }));
  }
  yield put(actions.disableSendButtonState(false));
}

function* apiFetchAsinsDetailsSaga(action) {
  const token = yield getJwt();
  const requestPayload = {
    arASINs: action.payload.arASINs,
  };
  let error = false;
  const apiRes = yield call(() => {
    return fetch(config.api.SERVER_URL + '/asin-details', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `${token}`,
      },
      body: stringify(requestPayload),
    })
      .then((resp) => {
        return resp.json();
      })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        error = true;
        sentry.captureException(e, {
          payload: requestPayload,
          error: e,
        });
      });
  });
  if (error) {
    yield put(actions.updateRequestError(true));
  }
  if (apiRes) {
    yield put(actions.updateAsinsDetails({ apiRes }));
  }
}

function* apiGetGoogleAuthUrl(action) {
  yield put(actions.enableLoading());
  const token = yield getJwt();
  let error = false;
  const isOpenDashboardRequest = _.get(
    action,
    'payload.isOpenDashboardRequest',
    false,
  );
  const apiRes = yield call(() => {
    return fetch(
      `${config.api.SERVER_URL}/google/auth?isOpenDashboardRequest=${isOpenDashboardRequest}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `${token}`,
          'x-parent-user-id': localStorage.getItem('x-parent-user-id') || '',
          'x-parent-user-email':
            localStorage.getItem('x-parent-user-email') || '',
        },
      },
    )
      .then((resp) => {
        return resp.json();
      })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        error = true;
        sentry.captureException(e, {
          error: e,
        });
      });
  });
  if (error) {
    yield put(actions.updateRequestError(true));
  }
  if (apiRes && apiRes.url) {
    if (isOpenDashboardRequest) {
      window.open(apiRes.url, '_blank', 'noreferrer');
    } else {
      window.location.href = apiRes.url;
    }
  }
  if (apiRes && apiRes.status === 'FAILED') {
    toast.error(apiRes.message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }
  yield put(actions.disableLoading());
}

function* revokeTokenSaga(action) {
  yield put(actions.enableLoading());
  const token = yield getJwt();
  let error = false;
  const apiRes = yield call(() => {
    return fetch(config.api.SERVER_URL + '/google/auth', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `${token}`,
        'x-parent-user-id': localStorage.getItem('x-parent-user-id') || '',
        'x-parent-user-email':
          localStorage.getItem('x-parent-user-email') || '',
      },
    })
      .then((resp) => {
        return resp.json();
      })
      .then((data) => {
        return data;
      })
      .catch((e) => {
        error = true;
        sentry.captureException(e, {
          error: e,
        });
      });
  });
  if (error) {
    yield put(actions.updateRequestError(true));
  }
  if (apiRes && apiRes.message === 'Successfully revoked Google account.') {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    localStorage.setItem(
      'userDetails',
      JSON.stringify({ ...userDetails, isGoogleConnected: false }),
    );
    yield put(actions.revokeTokenSuccess());
    notify.success(apiRes.message);
  }
  if (apiRes && apiRes.status === 'FAILED') {
    toast.error(apiRes.message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }
  yield put(actions.disableLoading());
}

function* apiGetAmazonAdvertisingAuthUrl(action) {
  yield put(actions.enableLoading());
  const headers = yield getHeaders();

  const apiRes = yield call(() => {
    return request(headers).get(`${config.api.SERVER_URL}/adv-api/auth`);
  });

  if (apiRes && apiRes.url) {
    window.location.href = apiRes.url;
  }
  if (apiRes && apiRes.status === 'FAILED') {
    toast.error(apiRes.message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }
  yield put(actions.disableLoading());
}

function* revokeAmazonAdvertisingSaga(action) {
  yield put(actions.enableLoading());
  const headers = yield getHeaders();

  const apiRes = yield call(() => {
    return request(headers).delete(`${config.api.SERVER_URL}/adv-api/auth`);
  });

  if (apiRes && apiRes.status === 'SUCCESS') {
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    localStorage.setItem(
      'userDetails',
      JSON.stringify({ ...userDetails, isAmazonAdvertisingConnected: false }),
    );
    yield put(actions.revokeAmazonAdvertisingSuccess());
    notify.success(apiRes.message);
  }
  if (apiRes && apiRes.status === 'FAILED') {
    toast.error(apiRes.message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }
  yield put(actions.disableLoading());
}

function* revokeAmazonSellerIntegrationSaga(action) {
  try {
    yield put(actions.enableLoading());
    const headers = yield getHeaders();
    const { region, id } = action.payload;

    const apiRes = yield call(() => {
      return request(headers).delete(`/sp-api/seller-central-integrations/${region}?id=${id}`);
    });

    if (apiRes && apiRes.status === 'OK') {
      yield put(actions.revokeAmazonSellerIntegrationSuccess({ id }));
      yield put(actions.fetchUserDetails());
      notify.success('Amazon Seller connection removed successfully');
    } else {
      notify.error('Failed to remove Amazon Seller connection');
    }
  } catch (error) {
    console.error('Error removing Amazon Seller connection:', error);
    notify.error('Failed to remove Amazon Seller connection');
  } finally {
    yield put(actions.disableLoading());
  }
}

function* apiGetUserSettingsSaga(action) {
  yield put(actions.enableLoading());

  const headers = yield getHeaders();
  const apiRes = yield call(() =>
    request(headers).get(`/users/settings`),
  );

  yield put(actions.setUserSettings(apiRes.data));
  yield put(actions.disableLoading());
}

function* apiUpdateUserSettings(action) {
  yield put(actions.enableLoading());

  const headers = yield getHeaders();
  const requestPayload = {
    direction: action.payload.direction,
    notes_enabled: action.payload.notesEnabled,
  };

  const apiRes = yield call(() =>
    request(headers).put(
      `/users/settings`,
      stringify(requestPayload),
    ),
  );

  yield put(actions.setUserSettings(apiRes.data));
  yield put(actions.disableLoading());
}

function* notificationAccessDeniedErrorMessage() {
  const errorMessage = yield select((state) => state.auth?.accessDeniedError);

  notify.error(
    errorMessage !== '' ? errorMessage : accessDeniedErrorMessages[0],
  );
}

function* signUpSaga(action: TAuthAction) {
  const callback = action.callback;
  try {
    yield put(actions.enableLoading());
    const { email, password, family_name, given_name, recaptchaToken } =
      action.payload;

    if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && !recaptchaToken) {
      yield put(
        actions.signUpError('reCAPTCHA verification failed. Please try again.'),
      );
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    const headers = yield getHeadersNoAuth();
    const response = yield call(() =>
      request(headers).post(
        '/auth/register',
        stringify({
          email,
          password,
          family_name,
          given_name,
          recaptchaToken,
        }),
      ),
    );

    if (response && response?.status === 'OK' && response?.user) {
      yield put(actions.signUpSuccess(response.user));
      yield put(actions.disableLoading());
      if (callback) callback(true);
      return true;
    }

    if (response && response.status === 'FAILED') {
      yield put(actions.signUpError(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    yield put(
      actions.signUpError('An unexpected error occurred during registration.'),
    );
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  } catch (error: any) {
    console.error('Registration error:', error);
    let errorMessage =
      'There was an error while creating your account, please try again later';

    if (error.response?.data) {
      const { status, message } = error.response.data;

      if (status === 'FAILED') {
        errorMessage = message;
      }
    }

    yield put(actions.signUpError(errorMessage));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  }
}

function* forgotPasswordSaga(action: TAuthAction) {
  const callback = action.callback;
  try {
    yield put(actions.enableLoading());
    const { email, recaptchaToken } = action.payload;

    // Verify reCAPTCHA token if enabled
    if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && !recaptchaToken) {
      yield put(
        actions.forgotPassError('reCAPTCHA verification failed. Please try again.'),
      );
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    const headers = yield getHeadersNoAuth();
    const response = yield call(() =>
      request(headers).post(
        '/auth/forgot-password',
        stringify({ email, recaptchaToken }),
      ),
    );

    if (response && response?.status === 'OK') {
      localStorage.setItem('verifyEmail', email);
      yield put(actions.forgotPasswordSuccess(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(true);
      return true;
    }

    if (response && response.status === 'FAILED') {
      yield put(actions.forgotPassError(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    yield put(
      actions.forgotPassError('An unexpected error occurred during password reset request.'),
    );
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  } catch (error: any) {
    console.error('Forgot password error:', error);
    let errorMessage = 'There was an error while requesting password reset, please try again later';

    if (error.response?.data) {
      const { status, message } = error.response.data;
      if (status === 'FAILED') {
        errorMessage = message;
      }
    }

    yield put(actions.forgotPassError(errorMessage));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  }
}

function* forgotPasswordConfirmSaga(action: TAuthAction) {
  const callback = action.callback;
  try {
    yield put(actions.enableLoading());
    const { email, confirmationCode, newPassword, recaptchaToken } = action.payload;

    // Add reCAPTCHA verification
    if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && !recaptchaToken) {
      yield put(
        actions.forgotPassError('reCAPTCHA verification failed. Please try again.'),
      );
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    const headers = yield getHeadersNoAuth();
    const response = yield call(() =>
      request(headers).post(
        '/auth/forgot-password-confirm',
        stringify({ email, confirmationCode, newPassword, recaptchaToken }),
      ),
    );

    if (response && response?.status === 'OK') {
      yield put(actions.forgotPasswordConfirmSuccess(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(true);
      return true;
    }

    if (response && response.status === 'FAILED') {
      yield put(actions.forgotPassError(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(false);
      return false;
    }

    yield put(actions.forgotPassError('An unexpected error occurred during password confirmation.'));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  } catch (error: any) {
    console.error('Forgot password confirm error:', error);
    let errorMessage = 'There was an error while confirming password reset, please try again later';

    if (error.response?.data) {
      const { status, message } = error.response.data;
      if (status === 'FAILED') {
        errorMessage = message;
      }
    }

    yield put(actions.forgotPassError(errorMessage));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  }
}

function* updateAccountSettingsSaga(action: TAuthAction) {
  const callback = action.callback;
  try {
    yield put(actions.enableLoading());
    const { 
      given_name, 
      family_name, 
      email,
      updateEmail,
      updatePassword,
      oldPassword,
      newPassword,
      currentPasswordForEmail,
    } = action.payload;

    const headers = yield getHeaders();
    const requestPayload = {
      given_name,
      family_name,
      email,
      updateEmail,
      ...(updateEmail && { currentPasswordForEmail }),
      updatePassword,
      ...(updatePassword && { oldPassword, newPassword }),
    };

    const response = yield call(() =>
      request(headers).put(
        '/auth/account-settings',
        stringify(requestPayload),
      ),
    );

    if (response && response?.status === 'OK') {
      try {
        const currentUser = yield Auth.currentAuthenticatedUser({ bypassCache: true });

        yield put(actions.fulfillUser({
          ...currentUser.attributes,
          given_name,
          family_name,
        }));
      } catch (refreshError: any) {
        yield put(actions.accountSettingsError(refreshError?.message));
      }

      yield put(actions.updateAccountSettingsSuccess(response.message));
      if (callback) callback(true, response);
      return true;
    }

    if (response && response.status === 'FAILED') {
      yield put(actions.accountSettingsError(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(false, response);
      return false;
    }

    yield put(actions.accountSettingsError('An unexpected error occurred while updating account settings.'));
    if (callback) callback(false);
    return false;
  } catch (error: any) {
    console.error('Update account settings error:', error);
    let errorMessage = 'There was an error while updating your account settings, please try again later';

    if (error.response?.data) {
      const { status, message } = error.response.data;
      if (status === 'FAILED' && message) {
        errorMessage = message;
      }
    } else if (error.response?.status === 400 && error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    yield put(actions.accountSettingsError(errorMessage));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  }
}

function* verifyEmailChangeSaga(action: TAuthAction) {
  const callback = action.callback;
  try {
    yield put(actions.enableLoading());
    const { token } = action.payload;

    const headers = yield getHeaders();
    const response = yield call(() =>
      request(headers).put(
        '/auth/verify-email-change',
        stringify({ token }),
      ),
    );

    // Handle successful response
    if (response && response.status === 'OK') {
      // Email verification successful
      yield put(actions.verifyEmailChangeSuccess(response.message));
      yield put(actions.disableLoading());

      if (callback) callback(true, response);
      return true;
    }

    // Handle error response
    if (response && response.status === 'FAILED') {
      yield put(actions.verifyEmailChangeError(response.message));
      yield put(actions.disableLoading());
      if (callback) callback(false, response);
      return false;
    }

    yield put(actions.verifyEmailChangeError('An error occurred while verifying your email'));
    if (callback) callback(false);
    return false;
  } catch (error: any) {
    console.error('Verify email change error:', error);
    let errorMessage = 'An error occurred while verifying your email';

    if (error.response?.data) {
      const { status, message } = error.response.data;
      if (status === 'FAILED' && message) {
        errorMessage = message;
      }
    } else if (error.response?.status === 400 && error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    yield put(actions.verifyEmailChangeError(errorMessage));
    yield put(actions.disableLoading());
    if (callback) callback(false);
    return false;
  }
}
function* updateConnectionNameSaga(action) {
  try {
    yield put(actions.enableLoading());
    const headers = yield getHeaders();
    const { id, name } = action.payload;
    
    const response = yield call(() =>
      request(headers).put(`/sp-api/${id}`, stringify({ name }))
    );

    if (response && response.status === 'OK') {
      yield put(actions.updateConnectionNameSuccess(response.responseData));
      yield put(actions.fetchUserDetails());
      notify.success('Connection name updated successfully');
    } else {
      notify.error('Failed to update connection name');
    }
  } catch (error) {
    console.error('Error updating connection name:', error);
    notify.error('Failed to update connection name');
  } finally {
    yield put(actions.disableLoading());
  }
}
